import React, { useEffect, useState } from "react";

const APP_ID = "1783276106320929";
const CONFIG_ID = process.env.REACT_APP_META_EMBEDDED_SIGNUP_CONFIG_ID || "1622135076224695";
const EXCHANGE_URL = process.env.REACT_APP_META_OAUTH_EXCHANGE_URL || "https://sl7bh3z733.execute-api.us-east-1.amazonaws.com";
const OAUTH_REDIRECT_URI = "https://www.granada-palestine.com/meta-embedded-signup/";

/*
 * This route is intentionally isolated from the public Granada site. It holds
 * only Meta's public app/configuration identifiers; it never receives or
 * stores access tokens, authorization codes, WhatsApp messages, or contacts.
 *
 * The user must stop at Meta's QR/phone-approval stage. A future server-side
 * callback (with an app secret stored outside the browser) is required before
 * anyone can complete the code exchange.
 */
export default function WhatsAppCoexistenceSignup() {
  const [sdkReady, setSdkReady] = useState(false);
  const [status, setStatus] = useState("Ready to open Meta Embedded Signup.");

  useEffect(() => {
    const receiveMessage = (event) => {
      if (![
        "https://www.facebook.com",
        "https://web.facebook.com",
      ].includes(event.origin)) return;

      let message = event.data;
      try {
        if (typeof message === "string") message = JSON.parse(message);
      } catch {
        return;
      }
      if (message?.type !== "WA_EMBEDDED_SIGNUP") return;

      if (message.event === "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING") {
        setStatus("Meta reports completion. Do not proceed further until the secure server-side exchange service is enabled.");
      } else {
        setStatus("Meta Embedded Signup is in progress.");
      }
    };

    window.fbAsyncInit = () => {
      window.FB.init({ appId: APP_ID, cookie: true, xfbml: false, version: "v26.0" });
      setSdkReady(true);
    };

    const sdk = document.createElement("script");
    sdk.async = true;
    sdk.defer = true;
    sdk.crossOrigin = "anonymous";
    sdk.src = "https://connect.facebook.net/en_US/sdk.js";
    document.body.appendChild(sdk);
    window.addEventListener("message", receiveMessage);

    return () => {
      window.removeEventListener("message", receiveMessage);
      sdk.remove();
      delete window.fbAsyncInit;
    };
  }, []);

  const launch = async () => {
    if (!sdkReady || !window.FB) {
      setStatus("Meta's SDK is still loading. Try again in a moment.");
      return;
    }
    let state;
    try {
      const session = await fetch(`${EXCHANGE_URL}/session`, { method: "POST", cache: "no-store" });
      const body = await session.json();
      if (!session.ok || !body.state) throw new Error("Secure signup session was unavailable.");
      state = body.state;
    } catch {
      setStatus("The secure signup service is unavailable. No Meta authorization was started.");
      return;
    }

    setStatus("Opening Meta Embedded Signup…");
    window.FB.login((response) => {
      if (response?.authResponse?.code) {
        // The one-time code is sent only to the dedicated HTTPS backend and is never rendered or persisted in the browser.
        fetch(`${EXCHANGE_URL}/oauth/exchange`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ code: response.authResponse.code, state }),
        })
          .then(async (result) => {
            const body = await result.json();
            const errorMessage = typeof body?.error === "string" ? body.error : body?.error?.message;
            if (!result.ok) throw new Error(errorMessage || "Secure authorization exchange failed.");
            setStatus("Authorization exchanged securely. Meta webhook setup and WABA subscription were requested. Do not start ads; wait for verification.");
          })
          .catch((error) => setStatus(`Secure authorization exchange failed: ${error.message || "unknown error"}. No token was retained in the browser.`));
      } else if (!response?.authResponse) {
        setStatus("Embedded Signup was closed or cancelled.");
      }
    }, {
      config_id: CONFIG_ID,
      state,
      response_type: "code",
      override_default_response_type: true,
      redirect_uri: OAUTH_REDIRECT_URI,
      extras: {
        setup: {},
        featureType: "whatsapp_business_app_onboarding",
        sessionInfoVersion: "3",
      },
    });
  };

  return (
    <main style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24, background: "#071523", color: "#f7fbff", fontFamily: "system-ui, sans-serif" }}>
      <section style={{ width: "min(620px, 100%)", padding: 32, border: "1px solid #274157", borderRadius: 16, background: "#0c2033" }}>
        <h1 style={{ marginTop: 0 }}>GRANADA WhatsApp coexistence</h1>
        <p>Official Meta Embedded Signup for the existing WhatsApp Business App number <strong>+972597350458</strong>.</p>
        <p>This page does not register, migrate, deregister, replace, or modify the number. Stop at Meta’s QR/mobile-approval step until instructed.</p>
        <button type="button" onClick={launch} disabled={!sdkReady} style={{ background: "#22b573", color: "#05150d", border: 0, borderRadius: 10, padding: "12px 18px", fontWeight: 700, cursor: sdkReady ? "pointer" : "wait" }}>
          Launch Embedded Signup
        </button>
        <p role="status" style={{ marginTop: 18, color: "#c6d7e7" }}>{status}</p>
      </section>
    </main>
  );
}
